import React, { useCallback, useMemo } from 'react';
import { FieldLabel } from '@puckeditor/core';
import styled from 'styled-components';

import { isSurfaceColorRole, resolveSurfaceColor } from '../tokens';
import type { SurfaceColorRole } from '../tokens';
import { hexColorSwatchValue } from '../hex-color';
import { HexColorError, useHexColorInput } from './use-hex-color-input';
import { SEGMENTED_SELECTED_BG, SEGMENTED_SELECTED_COLOR } from './segmented-control-style';
import { disabledSelectControlStyle, fieldControlStyle, selectControlStyle } from './field-control-style';

/**
 * The fallback color only. The background image and its size/position/repeat are flat props on the
 * blocks that support one (see `background-image.tsx`), so this stays a single-field object.
 */
export type BackgroundValue = {
  color: string;
};

export const DEFAULT_BACKGROUND: BackgroundValue = {
  // 'surface' resolves to `--dcm-color-surface` (fallback #ffffff) — no visual change,
  // but the default now follows the merchant theme rather than a baked-in hex.
  color: 'surface',
};

/** Starting color when the author switches from a theme role to a custom hex. */
const CUSTOM_DEFAULT = '#f5f6f7';

/** Options offered by the background color dropdown, in display order. */
const SURFACE_ROLE_OPTIONS: readonly { label: string; value: SurfaceColorRole }[] = [
  { label: 'Surface', value: 'surface' },
  { label: 'Muted', value: 'muted' },
];

const CUSTOM_OPTION_VALUE = 'custom';

/**
 * Converts a `BackgroundValue` into React inline style properties.
 *
 * Accepts `undefined` and emits no declarations for it. This is *not* backwards-compatibility
 * machinery -- it is the same unvalidated-stored-data rule the resolvers in this directory follow
 * (see `border.ts#borderStyle` and `resolveBorderColor`): persisted page data is JSON with no
 * runtime type enforcement, so a value violating the declared type can arrive here, and it must
 * never crash the surrounding page.
 *
 * Load-bearing rather than theoretical: `Section`/`FlexLayout` predate this field, Puck merges
 * `defaultProps` into saved props in the *editor* (`dist/index.js:10767`) but **not** on the
 * production `<Render>` path (`DropZoneRenderItem` passes `item.props` straight through), and
 * neither renderer has an error boundary -- so an already-published page with no `background` key
 * would white-screen entirely while still looking correct in the editor.
 *
 * Note this returns `{}` rather than the old `{ backgroundColor: <surface token> }` default: on
 * `FlexLayout` (an inner container) that default painted an opaque fill over whatever sat behind
 * it, which is why its call site used to guard the spread. Returning nothing keeps an absent key
 * visually identical to "no background", for both components, without a call-site conditional.
 *
 * `background` itself stays **required** on `SectionProps`/`FlexLayoutProps` -- the type governs
 * what authoring code may pass, this guard governs what stored JSON can actually contain.
 */
export function backgroundStyle(bg: BackgroundValue | undefined): React.CSSProperties {
  if (!bg) return {};
  return { backgroundColor: resolveSurfaceColor(bg.color) };
}

/** AI schema for the BackgroundValue object (the fallback color only; the image is its own flat props). */
export const BACKGROUND_AI_SCHEMA = {
  type: 'object',
  required: ['color'],
  properties: {
    color: {
      type: 'string',
      description:
        "Either a surface theme role ('none', 'surface', 'muted') or a hex value such as #ff0000. Prefer a role so the background follows merchant branding. 'none' is transparent.",
    },
  },
};

type BackgroundFieldProps = {
  field: { label?: string };
  name: string;
  value: BackgroundValue | undefined;
  onChange: (value: BackgroundValue) => void;
  readOnly?: boolean;
};

const inputStyle: React.CSSProperties = {
  ...fieldControlStyle,
};

const selectStyle: React.CSSProperties = {
  ...selectControlStyle,
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 8,
};

const ModeGroup = styled.div`
  display: flex;
  margin-top: 8px;
  border: 1px solid
    var(--dcm-cms-bg-field-mode-border-color, var(--puck-field-color-border, var(--puck-color-border, #d0d4d8)));
  border-radius: var(--dcm-cms-bg-field-mode-border-radius, var(--puck-field-radius, var(--puck-radius-m, 6px)));
  overflow: hidden;
`;

const ModeOption = styled.label<{ $selected: boolean }>`
  position: relative;
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  align-items: center;
  justify-content: center;
  padding: var(--dcm-cms-segmented-padding, 12px 8px);
  background: ${({ $selected }) =>
    $selected
      ? `var(--dcm-cms-bg-field-mode-selected-bg, ${SEGMENTED_SELECTED_BG})`
      : 'var(--dcm-cms-bg-field-mode-bg, var(--puck-field-color-bg, var(--puck-color-surface, #fff)))'};
  color: ${({ $selected }) =>
    $selected
      ? `var(--dcm-cms-bg-field-mode-selected-text-color, ${SEGMENTED_SELECTED_COLOR})`
      : 'var(--dcm-cms-bg-field-mode-text-color, var(--puck-field-color-text, var(--puck-color-text, #121516)))'};
  cursor: pointer;
  font-size: var(--dcm-cms-segmented-font-size, var(--puck-font-size-xxs, 14px));
  font-weight: var(--dcm-cms-segmented-font-weight, 400);
  text-align: center;

  & + & {
    border-left: 1px solid
      var(--dcm-cms-bg-field-mode-border-color, var(--puck-field-color-border, var(--puck-color-border, #d0d4d8)));
  }

  &:focus-within {
    outline: var(--puck-border-width-focus, 2px) solid
      var(--dcm-cms-bg-field-mode-focus-ring-color, var(--puck-color-focus-ring, #0b99e6));
    outline-offset: -2px;
    z-index: 1;
  }

  &:has(input:disabled) {
    cursor: default;
    color: var(
      --dcm-cms-bg-field-mode-disabled-text-color,
      var(--puck-field-color-text-disabled, var(--puck-color-text-secondary, #646d72))
    );
  }
`;

const ModeInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
`;

const BackgroundSelect = styled.select`
  &:focus-visible {
    outline: 2px solid var(--dcm-cms-bg-field-select-focus-ring-color, var(--puck-color-focus-ring, #0b99e6));
    outline-offset: 2px;
  }
`;

function isInheritedBackground(background: BackgroundValue): boolean {
  return background.color === 'none';
}

/** Puck field widget for the background color (Inherit is transparent, Custom picks a role or hex). */
export function BackgroundField({ field, name, value, onChange, readOnly }: BackgroundFieldProps): JSX.Element {
  const bg = useMemo(() => ({ ...DEFAULT_BACKGROUND, ...value }), [value]);

  const isInherited = isInheritedBackground(bg);
  const isCustom = !isSurfaceColorRole(bg.color);

  const colorSelectValue = isCustom ? CUSTOM_OPTION_VALUE : bg.color;

  const handleModeChange = useCallback(
    (mode: 'custom' | 'inherit') => {
      if (mode === 'inherit') {
        onChange({ ...bg, color: 'none' });
      } else if (isInherited) {
        // Custom starts on a custom hex (the dropdown reads "Custom"), not on a theme role.
        onChange({ ...bg, color: CUSTOM_DEFAULT });
      }
    },
    [bg, isInherited, onChange]
  );

  const handleColorSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const chosen = e.currentTarget.value;
      if (chosen === CUSTOM_OPTION_VALUE) {
        // Only seed the starting hex when not already in custom mode.
        if (!isCustom) onChange({ ...bg, color: CUSTOM_DEFAULT });
      } else {
        onChange({ ...bg, color: chosen });
      }
    },
    [isCustom, bg, onChange]
  );

  const handleColorChange = useCallback((color: string) => onChange({ ...bg, color }), [bg, onChange]);
  const hexInput = useHexColorInput(bg.color, handleColorChange);

  return (
    <FieldLabel label={field.label ?? name} readOnly={readOnly} el='div'>
      <ModeGroup role='radiogroup' aria-label={`${field.label ?? name} mode`}>
        <ModeOption $selected={isInherited}>
          <ModeInput
            type='radio'
            name={`${name}-mode`}
            value='inherit'
            checked={isInherited}
            onChange={() => handleModeChange('inherit')}
            disabled={readOnly}
            data-testid={`bg-mode-${name}-inherit`}
          />
          Inherit
        </ModeOption>
        <ModeOption $selected={!isInherited}>
          <ModeInput
            type='radio'
            name={`${name}-mode`}
            value='custom'
            checked={!isInherited}
            onChange={() => handleModeChange('custom')}
            disabled={readOnly}
            data-testid={`bg-mode-${name}-custom`}
          />
          Custom
        </ModeOption>
      </ModeGroup>

      {!isInherited && (
        <>
          {/* Color role row — semantic theme roles first, custom hex as the escape hatch. */}
          <div style={rowStyle}>
            <BackgroundSelect
              value={colorSelectValue}
              onChange={handleColorSelectChange}
              disabled={readOnly}
              aria-label={`${field.label ?? name} color role`}
              data-testid={`bg-color-role-${name}`}
              style={{ ...selectStyle, flex: 1, ...(readOnly && disabledSelectControlStyle) }}
            >
              {SURFACE_ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value} data-testid={`bg-color-role-${name}-${opt.value}`}>
                  {opt.label}
                </option>
              ))}
              <option value={CUSTOM_OPTION_VALUE} data-testid={`bg-color-role-${name}-custom`}>
                Custom
              </option>
            </BackgroundSelect>
          </div>

          {/* Custom hex inputs — only shown when author has chosen a custom color */}
          {isCustom && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
              <input
                type='color'
                value={hexColorSwatchValue(bg.color, CUSTOM_DEFAULT)}
                onChange={hexInput.handleSwatchChange}
                disabled={readOnly}
                aria-label={`${field.label ?? name} custom color swatch`}
                data-testid={`bg-color-swatch-${name}`}
                style={{
                  width: 32,
                  height: 32,
                  padding: 0,
                  border: '1px solid #d0d4d8',
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
              />
              <input
                type='text'
                value={hexInput.text}
                onChange={hexInput.handleChange}
                disabled={readOnly}
                aria-label={`${field.label ?? name} custom color value`}
                aria-invalid={hexInput.invalid || undefined}
                aria-describedby={hexInput.invalid ? hexInput.errorId : undefined}
                data-testid={`bg-color-text-${name}`}
                style={{
                  ...inputStyle,
                  flex: 1,
                  minWidth: 0,
                  fontFamily: 'var(--dcm-cms-bg-field-hex-font-family, monospace)',
                }}
              />
            </div>
          )}

          {isCustom && hexInput.invalid && <HexColorError id={hexInput.errorId} />}
        </>
      )}
    </FieldLabel>
  );
}
